import type { PricePublish, PriceWorksheet, PriceWorksheetRow } from '@morales/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../api';
import { useAuth } from '../auth';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { ErrorBox, QueryView } from '../components/ui';
import { formatDate, formatMoney, todayIso } from '../format';
import type { AppMessageKey } from '../messages';
import { useDebounced, useLeaveGuard, useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { parseNumber } from './StockPage';

type Typed = Record<string, string>; // "itemId:tier" -> what was typed
const keyOf = (itemId: string, tier: string) => `${itemId}:${tier}`;
type Line = PriceWorksheetRow & { typed: Record<string, string>; modeTyped: Record<string, Mode> };
type Mode = 'auto' | 'hand';
/** a tier price's mode as published: before decision 161 a price has none and is kept as hand */
const modeOf = (tr: { mode: Mode | null; price: number | null } | undefined): Mode | null => tr?.mode ?? (tr?.price !== null && tr?.price !== undefined ? 'hand' : null);

/** The price a tier cell holds: what was typed, else the price in force. */
export function cellPrice(l: Line, tier: string): number | null {
  const raw = l.typed[tier];
  if (raw !== undefined && raw.trim() !== '') return parseNumber(raw);
  return l.tiers.find((x) => x.code === tier)?.price ?? null;
}
/** Gross margin on the price, in percent of the price (what an accountant calls GM %). */
export function gmPct(price: number | null, cost: number | null): number | null {
  if (price === null || cost === null || price <= 0) return null;
  return Math.round(((price - cost) / price) * 1000) / 10;
}

type SortKey = 'item' | 'cost' | 'comp';
/** what gave today's cost (decision 160) */
const COST_SOURCE: Record<string, AppMessageKey> = { list: 'costSrcList', order: 'costSrcOrder', receipt: 'costSrcDock', invoice: 'costSrcBill', manual: 'costSrcTyped', import: 'costSrcOther' };

/** Price management on the sheet: every active item, its cost, sold and competitor, a column per tier; publish writes a dated list. */
export function PricesPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const owner = me?.role === 'owner';
  const qc = useQueryClient();
  const [q, setQ] = useSearchParam('q');
  const term = useDebounced(q.trim());
  const [typed, setTyped] = useState<Typed>({});
  // auto (follows cost) or hand (the owner's), chosen per cell (decision 161); a typed price is hand unless chosen auto
  const [modes, setModes] = useState<Record<string, Mode>>({});
  const [fillNote, setFillNote] = useState<string | null>(null);
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [done, setDone] = useState<{ count: number; unchanged: number } | null>(null);
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const sheet = useQuery({ queryKey: qk.priceWorksheet(term), queryFn: ({ signal }) => api.priceWorksheet(term, signal), placeholderData: keepPreviousData });
  // the rows kept in view by "Only changed rows" even after a price is typed back (so the cursor does not skip a row)
  const [kept, setKept] = useState<Set<string>>(new Set());
  const sorters = useMemo(
    () => ({
      item: byText<Line>((l) => l.itemName),
      cost: byNumber<Line>((l) => l.cost),
      comp: byNumber<Line>((l) => l.competitorLow),
    }),
    [],
  );
  const { sorted, head } = useSort<Line, SortKey>(sorters, { key: 'item', dir: 1 }, ['cost', 'comp']);

  const publish = useMutation({
    mutationFn: (v: { body: PricePublish & { approveFloor?: boolean }; key: string; sent: string[] }) => api.publishPrices(v.body, v.key),
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: (r, v) => {
      attempt.current = null;
      // only what was published is cleared: a price typed on a row the search hides stays typed
      setTyped((cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => !v.sent.includes(k))));
      setModes((cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => !v.sent.includes(k))));
      setFillNote(null);
      setDone({ count: r.count, unchanged: r.unchanged });
      void qc.invalidateQueries({ queryKey: ['pricing'] });
      void qc.invalidateQueries({ queryKey: ['items'] });
      // the invoice screen reads each customer's prices from here: a new invoice must charge the price just published
      void qc.invalidateQueries({ queryKey: ['customers', 'price-sheet'] });
    },
  });

  const changes = useMemo(() => {
    const rows = sheet.data?.rows ?? [];
    const out: { itemId: string; tierCode: string; price: number; mode: Mode; row: PriceWorksheetRow }[] = [];
    for (const k of new Set([...Object.keys(typed), ...Object.keys(modes)])) {
      const raw = typed[k];
      if (raw !== undefined && raw.trim() === '') continue;
      const [itemId, tierCode] = k.split(':') as [string, string];
      const row = rows.find((r) => r.itemId === itemId);
      if (!row) continue;
      const tr = row.tiers.find((x) => x.code === tierCode);
      const current = tr?.price ?? null;
      const currentMode = modeOf(tr);
      const chosen = modes[k];
      // a typed price is the owner's (hand) unless auto is chosen; auto chosen alone takes the engine's number
      const price = raw !== undefined ? parseNumber(raw) : chosen === 'auto' ? (tr?.suggested ?? null) : current;
      if (price === null || price < 0) continue;
      const mode: Mode = chosen ?? (raw !== undefined ? 'hand' : (currentMode ?? 'hand'));
      if (current === price && currentMode === mode) continue;
      out.push({ itemId, tierCode, price, mode, row });
    }
    return out;
  }, [typed, modes, sheet.data]);
  const belowCost = changes.filter((c) => c.row.cost !== null && c.price < c.row.cost);
  const underFloor = changes.filter((c) => {
    const f = c.row.tiers.find((x) => x.code === c.tierCode)?.floorPrice ?? null;
    return f !== null && c.price < f && !(c.row.cost !== null && c.price < c.row.cost);
  });
  const visible = new Set((sheet.data?.rows ?? []).map((r) => r.itemId));
  const typedKeys = Object.entries(typed).filter(([, raw]) => raw.trim() !== '');
  const hidden = typedKeys.filter(([k]) => !visible.has(k.split(':')[0]!)).length;
  const invalid = typedKeys.filter(([k, raw]) => visible.has(k.split(':')[0]!) && (parseNumber(raw) === null || (parseNumber(raw) ?? 0) < 0)).length;
  useLeaveGuard(owner && typedKeys.length > 0, t('pricesUnsaved'));

  const submit = (approveFloor: boolean) => {
    if (changes.length === 0 || belowCost.length > 0 || invalid > 0) return;
    // the invoices charge these from today: one more look before hundreds of prices change at once
    if (!window.confirm(t('publishConfirm').replace('{n}', String(changes.length)).replace('{floor}', String(underFloor.length)))) return;
    const body: PricePublish & { approveFloor?: boolean } = { prices: changes.map((c) => ({ itemId: c.itemId, tierCode: c.tierCode, price: c.price, mode: c.mode })), ...(approveFloor ? { approveFloor: true } : {}) };
    const serialized = JSON.stringify(body);
    if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
    publish.mutate({ body, key: attempt.current.key, sent: changes.map((c) => keyOf(c.itemId, c.tierCode)) });
  };
  const fillAll = (data: PriceWorksheet) => {
    const next: Typed = { ...typed };
    const nextModes = { ...modes };
    let held = 0;
    for (const row of data.rows)
      for (const tier of data.tiers) {
        const tr = row.tiers.find((x) => x.code === tier.code);
        const k = keyOf(row.itemId, tier.code);
        // a price the owner typed or chose stays; a move held for the owner (past the hold) is left for a look
        if (next[k] !== undefined || nextModes[k] !== undefined || tr?.suggested === null || tr?.suggested === undefined) continue;
        if (tr.held) {
          held += 1;
          continue;
        }
        // the engine's number, following cost from now on (auto, decision 161)
        if (tr.suggested !== tr.price || tr.mode !== 'auto') {
          next[k] = String(tr.suggested);
          nextModes[k] = 'auto';
        }
      }
    setTyped(next);
    setModes(nextModes);
    setFillNote(held ? t('fillHeldNote').replace('{n}', String(held)) : null);
  };

  const lines: Line[] = useMemo(() => {
    const rows = sheet.data?.rows ?? [];
    const tierCodes = sheet.data?.tiers.map((x) => x.code) ?? [];
    return rows.map((r) => ({
      ...r,
      typed: Object.fromEntries(tierCodes.filter((c) => typed[keyOf(r.itemId, c)] !== undefined).map((c) => [c, typed[keyOf(r.itemId, c)]!])),
      modeTyped: Object.fromEntries(tierCodes.filter((c) => modes[keyOf(r.itemId, c)] !== undefined).map((c) => [c, modes[keyOf(r.itemId, c)]!])),
    }));
  }, [sheet.data, typed, modes]);
  const changedIds = new Set(changes.map((c) => c.itemId));
  const shown = sorted(onlyChanged ? lines.filter((l) => changedIds.has(l.itemId) || kept.has(l.itemId)) : lines);

  const money = (v: number | null) => (v === null ? '' : formatMoney(v, intlLocale));
  const today = todayIso();
  const columns = (data: PriceWorksheet): SheetColumn<Line>[] => [
    {
      key: 'item',
      header: head('item', t('itemCol')),
      label: t('itemCol'),
      width: '220px',
      render: (l) => (
        <span>
          <Link to={`/items/${l.itemId}/intel`}>{l.itemName}</Link>
          <span className="cell-vendor"> · {l.packDesc ?? l.packUnit}</span>
        </span>
      ),
    },
    {
      key: 'cost',
      header: head('cost', t('cost')),
      label: t('cost'),
      width: '96px',
      align: 'right',
      render: (l) => {
        if (l.cost === null) return '';
        const moved = l.costPrior !== null && l.costPrior !== l.cost;
        return (
          <span title={moved ? `${t('costBefore')} ${money(l.costPrior)}` : undefined}>
            {moved ? <span className={l.cost > (l.costPrior ?? 0) ? 'move-up' : 'move-down'}>{l.cost > (l.costPrior ?? 0) ? '▲ ' : '▼ '}</span> : null}
            {money(l.cost)}
            {moved ? <span className="cell-sub">{t('costWas').replace('{n}', money(l.costPrior))}</span> : null}
          </span>
        );
      },
    },
    {
      // where today's cost came from and how old it is (decision 160): the sheet's "Cost source" and "Cost as of"
      key: 'from',
      header: t('costFromCol'),
      label: t('costFromCol'),
      width: '150px',
      render: (l) => {
        if (l.cost === null || !l.costDate) return '';
        const days = Math.round((Date.parse(`${today}T12:00:00Z`) - Date.parse(`${l.costDate}T12:00:00Z`)) / 86_400_000);
        return (
          <span>
            {[l.costVendor, l.costSource ? t(COST_SOURCE[l.costSource] ?? 'costSrcOther') : null].filter(Boolean).join(' · ')}
            <span className={`cell-sub${days > 7 ? ' stale' : ''}`}>
              {formatDate(l.costDate, intlLocale, 'date')}
              {days > 7 ? ` · ${t('costDaysOld').replace('{n}', String(days))}` : ''}
            </span>
          </span>
        );
      },
    },
    {
      key: 'comp',
      header: head('comp', t('competitorLowCol')),
      label: t('competitorLowCol'),
      width: '112px',
      align: 'right',
      render: (l) => (l.competitorLow === null ? '' : <span title={l.competitorName ?? undefined}>{money(l.competitorLow)}</span>),
    },
    {
      key: 'usda',
      header: t('usdaCol'),
      label: t('usdaCol'),
      width: '128px',
      align: 'right',
      // USDA Market News for the linked commodity (decision 144): the latest day's range, its market and package on hover
      render: (l) =>
        !l.usda || (l.usda.low === null && l.usda.high === null) ? (
          ''
        ) : (
          <span title={`${l.usda.market} · ${l.usda.package ?? ''} · ${l.usda.date}`}>
            {l.usda.low !== null && l.usda.high !== null && l.usda.low !== l.usda.high ? `${money(l.usda.low)}–${money(l.usda.high)}` : money(l.usda.low ?? l.usda.high)}
          </span>
        ),
    },
    ...data.tiers.flatMap((tier): SheetColumn<Line>[] => [
      {
        key: `price:${tier.code}`,
        header: tier.name,
        label: tier.name,
        width: '96px',
        align: 'right',
        render: (l) => {
          const raw = l.typed[tier.code];
          const v = cellPrice(l, tier.code);
          if (raw !== undefined && raw.trim() !== '' && (v === null || v < 0)) return <span title={t('invalidPrices')}>{raw}</span>;
          const tr = l.tiers.find((x) => x.code === tier.code);
          // a changed cell says what it was; a move past the hold says what the engine wants (decision 161)
          const was = raw !== undefined && tr?.price !== null && tr?.price !== undefined && v !== tr.price ? t('costWas').replace('{n}', money(tr.price)) : null;
          const held = raw === undefined && tr?.held ? t('priceHeld').replace('{n}', money(tr.suggested ?? 0)) : null;
          return (
            <span>
              {money(v)}
              {was || held ? <span className={`cell-sub${held ? ' stale' : ''}`}>{was ?? held}</span> : null}
            </span>
          );
        },
        edit: owner
          ? {
              kind: 'number',
              get: (l) => {
                const v = cellPrice(l, tier.code);
                return v === null ? '' : String(v);
              },
              set: (l, v) => ({ ...l, typed: { ...l.typed, [tier.code]: v } }),
            }
          : undefined,
        canEdit: () => owner && !publish.isPending,
        cellClass: (l) => {
          const v = cellPrice(l, tier.code);
          const tr = l.tiers.find((x) => x.code === tier.code);
          const parts: string[] = ['tier-cell'];
          const raw = l.typed[tier.code];
          if (raw !== undefined) parts.push('unsaved');
          if (raw !== undefined && raw.trim() !== '' && (v === null || v < 0)) parts.push('bad');
          else if (v !== null && l.cost !== null && v < l.cost) parts.push('bad');
          else if (v !== null && tr?.floorPrice !== null && tr?.floorPrice !== undefined && v < tr.floorPrice) parts.push('warn');
          return parts.join(' ');
        },
      },
      {
        // auto: the price follows cost by itself; hand: the owner's, kept (decision 161)
        key: `mode:${tier.code}`,
        header: (
          <span className="th-sub-col">
            {t('followsCol')}
            <span className="sr-only"> {tier.name}</span>
          </span>
        ),
        label: `${t('followsCol')} ${tier.name}`,
        hint: t('followsHint'),
        width: '70px',
        render: (l) => {
          const tr = l.tiers.find((x) => x.code === tier.code);
          const m = l.modeTyped[tier.code] ?? (l.typed[tier.code] !== undefined ? 'hand' : modeOf(tr));
          return m ? t(m === 'auto' ? 'priceAuto' : 'priceHand') : '';
        },
        edit: owner
          ? {
              kind: 'pick',
              options: [
                { value: 'auto', label: t('priceAuto'), hint: t('priceAutoShort') },
                { value: 'hand', label: t('priceHand'), hint: t('priceHandShort') },
              ],
              get: (l) => l.modeTyped[tier.code] ?? modeOf(l.tiers.find((x) => x.code === tier.code)) ?? '',
              set: (l, v) => (v === 'auto' || v === 'hand' ? { ...l, modeTyped: { ...l.modeTyped, [tier.code]: v } } : null),
            }
          : undefined,
        canEdit: (l) => owner && !publish.isPending && l.tiers.find((x) => x.code === tier.code)?.suggested !== null,
        cellClass: (l) => (l.modeTyped[tier.code] !== undefined ? 'unsaved' : undefined),
      },
      {
        key: `eng:${tier.code}`,
        header: (
          <span className="th-sub-col">
            {t('engineCol')}
            <span className="sr-only"> {tier.name}</span>
          </span>
        ),
        label: `${t('engineCol')} ${tier.name}`,
        hint: t('engineCol'),
        width: '84px',
        align: 'right',
        render: (l) => {
          const s = l.tiers.find((x) => x.code === tier.code)?.suggested ?? null;
          return s === null ? '' : money(s);
        },
        cellClass: () => 'src-pattern',
      },
      {
        key: `gm:${tier.code}`,
        header: (
          <span className="th-sub-col">
            {t('gmCol')}
            <span className="sr-only"> {tier.name}</span>
          </span>
        ),
        label: `${t('gmCol')} ${tier.name}`,
        width: '64px',
        align: 'right',
        render: (l) => {
          const g = gmPct(cellPrice(l, tier.code), l.cost);
          return g === null ? '' : `${g.toFixed(1)}%`;
        },
        cellClass: (l) => {
          const v = cellPrice(l, tier.code);
          return v !== null && l.cost !== null && v < l.cost ? 'bad' : undefined;
        },
      },
    ]),
  ];

  return (
    <div className="desk-page prices">
      <header className="desk-head">
        <div>
          <h1>{t('pricesTitle')}</h1>
          <p className="muted">{t('pricesHint')}</p>
        </div>
        <Link to="/prices/list" className="btn btn-secondary btn-sm">
          {t('priceList')}
        </Link>
      </header>
      <div className="toolbar">
        <input className="tool-search" type="search" placeholder={t('findItem')} aria-label={t('findItem')} value={q} onChange={(e) => setQ(e.target.value)} />
        {owner ? (
          <label className="tool check">
            <input
              type="checkbox"
              checked={onlyChanged}
              onChange={(e) => {
                setOnlyChanged(e.target.checked);
                setKept(new Set(changes.map((c) => c.itemId)));
              }}
            />
            {t('onlyChanged')}
          </label>
        ) : null}
        <span className="spacer" />
        {owner && sheet.data ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fillAll(sheet.data)}>
            {t('useEngineAll')}
          </button>
        ) : null}
        {owner ? (
          underFloor.length > 0 && belowCost.length === 0 ? (
            <button type="button" className="btn btn-warn btn-sm" disabled={publish.isPending || invalid > 0} onClick={() => submit(true)}>
              {t('approveFloorPrices')}
            </button>
          ) : (
            <button type="button" className="btn btn-primary btn-sm" disabled={publish.isPending || changes.length === 0 || belowCost.length > 0 || invalid > 0} onClick={() => submit(false)}>
              {t('publishN').replace('{n}', String(changes.length))}
            </button>
          )
        ) : null}
      </div>
      <dl className="totals-strip" data-testid="price-totals">
        <div>
          <dt>{t('itemsCol')}</dt>
          <dd>{lines.length}</dd>
        </div>
        <div>
          <dt>{t('changesCol')}</dt>
          <dd>{changes.length}</dd>
        </div>
        <div className={belowCost.length > 0 ? 'bad' : undefined}>
          <dt>{t('belowCost')}</dt>
          <dd>{belowCost.length}</dd>
        </div>
        <div className={underFloor.length > 0 ? 'warn' : undefined}>
          <dt>{t('underFloor')}</dt>
          <dd>{underFloor.length}</dd>
        </div>
        <div>
          <dt>{t('lastPublished')}</dt>
          <dd>{sheet.data?.lastPublished ? formatDate(sheet.data.lastPublished, intlLocale, 'date') : '—'}</dd>
        </div>
      </dl>
      {hidden > 0 ? (
        <p className="warn-band" role="status">
          {t('typedHidden').replace('{n}', String(hidden))}
        </p>
      ) : null}
      {fillNote ? <p className="note">{fillNote}</p> : null}
      {done ? (
        <p className="note" role="status">
          {t('published')}: {done.count} {t('pricesPublished')}
          {done.unchanged ? ` · ${done.unchanged} ${t('nothingChanged').toLowerCase()}` : ''}
        </p>
      ) : null}
      {publish.isError ? <ErrorBox error={publish.error} /> : null}
      <QueryView query={sheet}>
        {(data) =>
          shown.length === 0 ? (
            <p className="muted pick-first">{t('noResults')}</p>
          ) : (
            <Sheet
              label={t('pricesTitle')}
              columns={columns(data)}
              rows={shown}
              rowKey={(l) => l.itemId}
              rowLabel={(l) => l.itemName}
              stickyFirst
              minWidth={`${520 + data.tiers.length * 250}px`}
              onRowsChange={(next, at) => {
                const l = next[at.row]!;
                if (at.col.startsWith('mode:')) {
                  const tier = at.col.slice('mode:'.length);
                  const chosen = l.modeTyped[tier];
                  const k = keyOf(l.itemId, tier);
                  const tr = l.tiers.find((x) => x.code === tier);
                  setDone(null);
                  if (onlyChanged) setKept((cur) => new Set(cur).add(l.itemId));
                  setModes((cur) => {
                    const rest = { ...cur };
                    delete rest[k];
                    // the mode already in force, with no price typed, is no change
                    return !chosen || (chosen === modeOf(tr) && typed[k] === undefined) ? rest : { ...rest, [k]: chosen };
                  });
                  return;
                }
                if (!at.col.startsWith('price:')) return;
                const tier = at.col.slice('price:'.length);
                const raw = l.typed[tier] ?? '';
                const current = l.tiers.find((x) => x.code === tier)?.price ?? null;
                const k = keyOf(l.itemId, tier);
                setDone(null);
                if (onlyChanged) setKept((cur) => new Set(cur).add(l.itemId));
                setTyped((cur) => {
                  const rest = { ...cur };
                  delete rest[k];
                  // clearing the cell or typing the price already in force is no change; a typo stays, red, until fixed
                  if (raw.trim() === '') return rest;
                  const n = parseNumber(raw);
                  return n !== null && n === current ? rest : { ...rest, [k]: raw };
                });
                // typed back to the price in force: the cell is left as it is, its mode too
                if (raw.trim() !== '' && parseNumber(raw) === current) setModes((cur) => Object.fromEntries(Object.entries(cur).filter(([key]) => key !== k)));
              }}
            />
          )
        }
      </QueryView>
    </div>
  );
}
